import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useNavigate, useSearchParams } from "react-router"
import { useArticleChildren } from "../../../api/articlesApi"
import type { ProductArticleRow } from "../../../api/types/masterData"
import { formatQuantity } from "../../../utils/format"
import {
  EntitySubTable,
  type SubTableColumn,
} from "../../shared/EntitySubTable"
import { McPill } from "../../shared/McPill"

export function ArticleBomSection() {
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const navigate = useNavigate()
  const { translate, currentLanguage } = useTranslation()

  const { data, hasNextPage, fetchNextPage, isLoading } = useArticleChildren(
    id,
    { limit: 20 },
  )

  const children = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total

  const columns: SubTableColumn<ProductArticleRow>[] = [
    {
      key: "position",
      header: "#",
      cell: (a) =>
        a.positionNumber != null ? (
          <span className="font-mono">{a.positionNumber}</span>
        ) : (
          <span className="text-grey5">—</span>
        ),
      className: "w-10",
      hideBelow: "280px",
    },
    {
      key: "articleNumber",
      header: translate("MC_MD_PANEL_COL_ARTICLE"),
      cell: (a) => <span className="font-mono">{a.articleNumber}</span>,
      className: "w-60",
    },
    {
      key: "name",
      header: translate("MC_MD_PANEL_COL_DESCRIPTION"),
      cell: (a) => <span className="block max-w-xs truncate">{a.name}</span>,
    },
    {
      key: "quantity",
      header: translate("MC_MD_PANEL_COL_QTY"),
      cell: (a) =>
        a.quantity != null ? (
          <span className="font-mono">
            {formatQuantity(a.quantity, a.unit, currentLanguage)}
          </span>
        ) : (
          <span className="text-grey5">—</span>
        ),
      className: "w-20",
      hideBelow: "360px",
    },
    {
      key: "restricted",
      header: translate("MC_MD_PANEL_COL_RESTRICTED"),
      cell: (a) =>
        a.restrictedSubstancesCount > 0 ? (
          <McPill tone="attention">{a.restrictedSubstancesCount}</McPill>
        ) : (
          <span className="text-grey5">—</span>
        ),
      className: "w-24",
      hideBelow: "480px",
    },
  ]

  return (
    <EntitySubTable
      title={translate("MC_MD_PANEL_BOM_TITLE")}
      items={children}
      columns={columns}
      keyOf={(a) => a.id}
      hasMore={!!hasNextPage}
      onFetchMore={fetchNextPage}
      isLoading={isLoading}
      total={total}
      onRowClick={(a) => navigate(`../articles?id=${a.id}&source=nav`)}
    />
  )
}
